import React, { useState, useEffect } from "react";
import { X, FileText, Layers, Search, ChevronRight } from "lucide-react";
import useFinance from "../../../hooks/useFinance";

export default function TemplateModal({ isOpen, onClose, onSubmit }) {
  const { fetchTemplates } = useFinance();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    table_name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, selectedCategory]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await fetchTemplates(
        selectedCategory === "all" ? null : selectedCategory
      );
      if (result.ok) {
        setTemplates(result.templates);
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: "all", label: "전체" },
    { value: "accounting", label: "회계" },
    { value: "sales", label: "매출" },
    { value: "expense", label: "비용" },
    { value: "inventory", label: "재고" },
    { value: "custom", label: "커스텀" },
  ];

  const filteredTemplates = templates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validate = () => {
    const newErrors = {};

    if (!selectedTemplate) {
      newErrors.template = "템플릿을 선택해주세요.";
    }

    if (!formData.table_name.trim()) {
      newErrors.table_name = "테이블 이름은 필수입니다.";
    } else if (formData.table_name.length > 255) {
      newErrors.table_name = "테이블 이름은 255자를 초과할 수 없습니다.";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "설명은 1000자를 초과할 수 없습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit({
        template_id: selectedTemplate.id,
        ...formData,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setFormData({ table_name: "", description: "" });
    setErrors({});
    setSearchTerm("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-4 rounded-t-xl flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="w-6 h-6" />
              <h2 className="text-xl font-bold">템플릿에서 테이블 생성</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Template List */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="p-6 flex-shrink-0">
              {/* Category Filter */}
              <div className="mb-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="템플릿 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Template Grid */}
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">
                            {template.name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {template.description || "설명이 없습니다"}
                          </p>
                          {template.category && (
                            <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              {template.category}
                            </span>
                          )}
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <ChevronRight className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}

                  {filteredTemplates.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>템플릿이 없습니다.</p>
                    </div>
                  )}
                </div>
              )}

              {errors.template && (
                <p className="mt-4 text-sm text-red-600">{errors.template}</p>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="w-1/2 p-6 flex flex-col">
            {selectedTemplate ? (
              <div className="flex flex-col h-full">
                <div className="space-y-6 flex-1">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h3 className="font-medium text-indigo-900 mb-2">
                      선택한 템플릿
                    </h3>
                    <p className="text-sm text-indigo-700">
                      {selectedTemplate.name}
                    </p>
                    {selectedTemplate.template_schema && (
                      <div className="mt-3 text-xs text-indigo-600">
                        {(() => {
                          try {
                            const schema =
                              typeof selectedTemplate.template_schema ===
                              "string"
                                ? JSON.parse(selectedTemplate.template_schema)
                                : selectedTemplate.template_schema;
                            return (
                              <>
                                <p>• {schema.columns?.length || 0}개의 컬럼</p>
                                <p>
                                  • {schema.aggregations?.length || 0}개의 집계
                                  설정
                                </p>
                              </>
                            );
                          } catch (e) {
                            console.error("Template schema parse error:", e);
                            return <p>템플릿 정보를 불러올 수 없습니다.</p>;
                          }
                        })()}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="table_name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      테이블 이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="table_name"
                      type="text"
                      value={formData.table_name}
                      onChange={(e) =>
                        setFormData({ ...formData, table_name: e.target.value })
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        errors.table_name ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="예: 2024년 매출 관리"
                      disabled={submitting}
                    />
                    {errors.table_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.table_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      설명
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        errors.description
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="테이블에 대한 설명을 입력하세요"
                      rows={4}
                      disabled={submitting}
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.description}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.description.length}/1000
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submitting}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? "생성 중..." : "생성"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Layers className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>왼쪽에서 템플릿을 선택하세요</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
